
/*----------------------------------------------------

  modal.js

----------------------------------------------------*/
/*----------------------------------------------------
  Dynamic modal shell & lifecycle
----------------------------------------------------*/

// get default dynamic modal shell markup
function get_dynamic_modal_shell(modal_id) {
  return `
    <button class="modal-box__close" commandfor="${modal_id}" command="close" aria-label="Close"></button>
    <div class="modal-box__nav"></div>
    <div class="modal-box__content"></div>
  `;
}

// ensure a modal has the required shell for dynamic injected content
function ensure_dynamic_modal_shell(modal) {
  modal.classList.add('modal-box');

  if (!modal.querySelector('.modal-box__close') || !modal.querySelector('.modal-box__content') || !modal.querySelector('.modal-box__nav')) {
    modal.innerHTML = get_dynamic_modal_shell(modal.id);
  }

  if (!modal._has_reset_listener) {
    modal.addEventListener('close', () => {
      reset_dynamic_modal(modal);
    });
    modal._has_reset_listener = true;
  }

  return modal;
}

// ensure fallback modal exists and has the required shell
function ensure_fallback_modal() {
  let modal = document.getElementById('modal-box');

  if (!modal) {
    modal = document.createElement('dialog');
    modal.id = 'modal-box';
    modal.setAttribute('data-modal-dynamic', '');
    document.body.appendChild(modal);
  }

  return ensure_dynamic_modal_shell(modal);
}

// reset dynamic modal content, classes and state on close
function reset_dynamic_modal(modal) {
  clear_modal_classes(modal);

  if (modal._has_custom_shell) {
    modal.innerHTML = get_dynamic_modal_shell(modal.id);
    delete modal._has_custom_shell;
  } else {
    const content = get_modal_content_container(modal);
    if (content) {
      content.innerHTML = '';
    }
  }

  delete modal._gallery_current;
  delete modal._gallery_nodes;
}

// resolve modal from trigger or return fallback modal
function get_modal_from_trigger(trigger) {
  const id = trigger.getAttribute('commandfor');
  return id ? document.getElementById(id) : ensure_fallback_modal();
}

// check if modal can safely receive dynamic injected content
function is_dynamic_modal(modal) {
  if (!modal) return false;

  return modal.id === 'modal-box' || modal.hasAttribute('data-modal-dynamic');
}


/*----------------------------------------------------
  Content helpers
----------------------------------------------------*/

// inject close button into HTML content if placeholder exists
function inject_close(source, modal_id = 'modal-box') {
  if (source.includes('[close]')) {
    return source.replace(
      '[close]',
      `<button class="modal__close--incontent" commandfor="${modal_id}" command="close" aria-label="Close"></button>`
    );
  }
  return source;
}

// preload an image and resolve when ready
function preload_image(src) {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => resolve();
    img.onerror = () => resolve();

    img.src = src;
  });
}

// remove any dynamically added modal classes
function clear_modal_classes(modal) {
  if (!modal._modal_classes || !modal._modal_classes.length) return;

  modal._modal_classes.forEach(c => modal.classList.remove(c));
  delete modal._modal_classes;
}

// apply dynamic modal classes and track them for cleanup
function apply_modal_classes(modal, new_class = []) {
  if (!new_class.length) return;

  modal._modal_classes = [...new_class];
  new_class.forEach(c => modal.classList.add(c));
}

// get modal content container
function get_modal_content_container(modal) {
  return modal?.querySelector('.modal-box__content') || null;
}

// get modal nav container
function get_modal_nav_container(modal) {
  return modal?.querySelector('.modal-box__nav') || null;
}

// check if injected HTML is replacing the modal shell
function is_full_modal_markup(html) {
  return html.includes('class="modal-box__content"') ||
         html.includes("class='modal-box__content'") ||
         html.includes('class="modal-box__header"') ||
         html.includes("class='modal-box__header'");
}


/*----------------------------------------------------
  URL helpers
----------------------------------------------------*/

// check if URL is an image
function is_image_url(url) {
  const image_extensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'];
  const extension = url.split('.').pop().toLowerCase();
  return image_extensions.includes(extension);
}

// check if URL is embeddable video (YouTube, Vimeo etc.)
function is_video_embed_url(url) {
  const video_domains = ['youtube.com', 'vimeo.com', 'dailymotion.com'];
  try {
    const url_object = new URL(url);
    const domain = url_object.hostname;
    return video_domains.some(video_domain => domain.includes(video_domain));
  } catch (e) {
    return false;
  }
}

// check if URL is a direct video file
function is_video_url(url) {
  const supported_formats = ['mp4', 'webm', 'ogg', 'mov'];
  const extension = url.split('.').pop().toLowerCase();
  return supported_formats.includes(extension);
}

// convert video URL into embed URL
function convert_to_embed_url(original_url) {
  const url_object = new URL(original_url);
  const hostname = url_object.hostname;
  let embed_url = '';

  if (hostname.includes('youtube.com')) {
    const video_id = url_object.searchParams.get('v');
    embed_url = video_id ? `https://www.youtube.com/embed/${video_id}?autoplay=1` : null;
  } else if (hostname.includes('vimeo.com')) {
    const video_id = url_object.pathname.split('/').pop();
    embed_url = video_id ? `https://player.vimeo.com/video/${video_id}?autoplay=1` : null;
  } else if (hostname.includes('dailymotion.com')) {
    const video_id = url_object.pathname.split('/').pop();
    embed_url = video_id ? `https://www.dailymotion.com/embed/video/${video_id}?autoplay=1` : null;
  }

  return embed_url;
}


/*----------------------------------------------------
  Gallery / lightbox
----------------------------------------------------*/

// get valid gallery link nodes from a gallery container
function get_gallery_nodes(gallery) {
  let gallery_nodes = gallery.querySelectorAll('a:not(:empty)');
  return Array.from(gallery_nodes).filter(node => {
    return node.querySelector('img') || node.querySelector('picture');
  });
}

// get current gallery item index from its gallery container
function get_gallery_index(element) {
  const gallery = element.closest('[data-gallery]');
  if (!gallery) return -1;

  const gallery_nodes = get_gallery_nodes(gallery);
  return gallery_nodes.indexOf(element);
}

// build gallery navigation and bind navigation handler
function create_gallery_navigation(element) {
  const gallery = element.closest('[data-gallery]');
  if (!gallery) return '';

  const modal = get_modal_from_trigger(element);
  const gallery_nodes = get_gallery_nodes(gallery);

  modal._gallery_nodes = gallery_nodes;

  if (gallery_nodes.length <= 1) return '';

  modal.removeEventListener('click', handle_lightbox_nav);
  modal.addEventListener('click', handle_lightbox_nav);

  return `
    <span class="modal--lightbox__nav" data-dir="prev" aria-label="Previous Slide"></span>
    <span class="modal--lightbox__nav" data-dir="next" aria-label="Next Slide"></span>
  `;

}


/*----------------------------------------------------
  Content resolution
----------------------------------------------------*/

// resolve modal content and classes based on link target
async function get_modal_contents(element = null, target = null) {
  let new_class = [];
  let new_content = '';
  let is_lightbox = false;

  const modal = get_modal_from_trigger(element);
  const modal_id = modal?.id || 'modal-box';

  target = (element !== null && target === null)
    ? (element.dataset.modal || element.getAttribute('href'))
    : target;

  if (!target) {
    return { new_content: '<p>Sorry, this content could not be loaded.</p>', new_class };
  }

  // inline HTML
  if (target.startsWith('#')) {
    const source_element = document.querySelector(target);
    if (source_element) {
      new_content = inject_close(source_element.innerHTML, modal_id);
    } else {
      new_content = '<p>Sorry, this content could not be found.</p>';
    }

  // image
  } else if (is_image_url(target)) {
    modal._gallery_current = get_gallery_index(element);

    new_content = '<img src="' + target + '" />';
    new_nav = create_gallery_navigation(element);
    is_lightbox = true;

  // video file
  } else if (is_video_url(target)) {
    modal._gallery_current = get_gallery_index(element);

    const extension = target.split('.').pop().toLowerCase();

    new_content = `
      <video autoplay loop playsinline muted>
        <source src="${target}" type="video/${extension}">
      </video>
    `;

    new_nav = create_gallery_navigation(element);
    is_lightbox = true;

  // instagram
  } else if (target.includes('instagram')) {
    const parts = target.split('/');
    const last_part = parts[parts.length - 2];
    const embed = build_instagram_embed(last_part);

    new_content = embed || '<p>Sorry, this embed could not be loaded.</p>';
    is_lightbox = true;
    new_class.push('modal--instagram');

  // iframe/video embed
  } else if (is_video_embed_url(target)) {
    const embed_url = convert_to_embed_url(target);

    if (embed_url) {
      new_content = '<iframe src="' + embed_url + '" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>';
    } else {
      new_content = '<p>Sorry, this video could not be embedded.</p>';
    }

    is_lightbox = true;

  // ajax/html
  } else {
    const ajax_url = target + (target.indexOf('?') === -1 ? '?' : '&') + 'ajax=true';

    try {
      const res = await fetch(ajax_url);

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const text = (await res.text()).trim();

      if (/^<!DOCTYPE html>/i.test(text) || /^<html/i.test(text)) {
        new_content = `<iframe src="${ajax_url}" frameborder="0" width="100%" height="100%"></iframe>`;
        new_class.push('modal--page');
      } else {
        new_content = inject_close(text, modal_id);
      }
    } catch (err) {
      console.log('AJAX fail', err);
      new_content = '<p>Sorry, this content could not be loaded.</p>';
    }
  }

  // caption
  if (element !== null && element.dataset.modalcaption) {
    new_content += '<div class="modal--lightbox__caption">' + element.dataset.modalcaption + '</div>';
  }

  // if lightbox, add class
  if (is_lightbox) {
    new_class.push('modal--lightbox');
  }

  return { new_content, new_nav, new_class };
}


/*----------------------------------------------------
  Modal content injection
----------------------------------------------------*/

// inject content and classes into modal, optionally opening it
function inject_modal_content(modal, new_content, new_nav, new_class = [], open_modal = true) {
  clear_modal_classes(modal);

  if (is_full_modal_markup(new_content)) {
    modal.innerHTML = new_content;
    modal._has_custom_shell = true;
  } else {
    const content = get_modal_content_container(modal);
    const nav = get_modal_nav_container(modal);
    if (!content) return;

    content.innerHTML = new_content;

    if(nav){
      nav.innerHTML = new_nav;
    }

    delete modal._has_custom_shell;
  }

  apply_modal_classes(modal, new_class);

  if (new_class.includes('modal--instagram')) {
    load_instagram_embed_script();
  }

  if (open_modal && !modal.open) {
    modal.showModal();
  }
}

// handle gallery next/prev navigation and update modal content
function handle_lightbox_nav(ev) {
  const nav = ev.target.closest('.modal--lightbox__nav');
  if (!nav) return;

  ev.stopPropagation();

  const modal = ev.currentTarget;
  const gallery_nodes = modal._gallery_nodes;
  if (!gallery_nodes || !gallery_nodes.length) return;

  const direction = nav.dataset.dir;

  let to_show_index = Number.isInteger(modal._gallery_current)
    ? modal._gallery_current
    : 0;

  to_show_index += (direction === 'next') ? 1 : -1;
  to_show_index = (to_show_index + gallery_nodes.length) % gallery_nodes.length;

  const modal_element = gallery_nodes[to_show_index];

  if (!modal_element) {
    modal.close();
    return;
  }

  const target = modal_element.dataset.modal || modal_element.getAttribute('href');

  const update_modal = () => {
    get_modal_contents(modal_element, target).then(({ new_content, new_nav, new_class }) => {
      requestAnimationFrame(() => {
        inject_modal_content(modal, new_content, new_nav, new_class, false);
        modal._gallery_current = to_show_index;
      });
    });
  };

  if (is_image_url(target)) {
    preload_image(target)
      .then(update_modal)
      .catch(update_modal);
    return;
  }

  update_modal();
}


/*----------------------------------------------------
  Event listeners
----------------------------------------------------*/

// handle Invoker API commands for dynamic modal content
document.addEventListener('command', async (e) => {
  if (e.command !== 'show-modal') return;

  const trigger = e.source;
  if (!trigger?.dataset.modal) return;

  const modal_id = trigger.getAttribute('commandfor');
  if (!modal_id) return;

  const modal = document.getElementById(modal_id);
  if (!modal) return;

  if (!is_dynamic_modal(modal)) return;

  ensure_dynamic_modal_shell(modal);

  const { new_content, new_nav, new_class } = await get_modal_contents(trigger, trigger.dataset.modal);
  inject_modal_content(modal, new_content, new_nav, new_class, false);
}, { capture: true });

// handle link clicks for fallback modal content
document.addEventListener('click', async (e) => {
  const link = e.target.closest('a[data-modal], [data-gallery] a:not(:empty)');
  if (!link) return;

  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

  e.preventDefault();

  const { new_content, new_nav, new_class } = await get_modal_contents(link);
  const modal = get_modal_from_trigger(link);

  if (!is_dynamic_modal(modal)) return;

  ensure_dynamic_modal_shell(modal);

  inject_modal_content(modal, new_content, new_nav, new_class, true);
});


/*----------------------------------------------------
  External embeds
----------------------------------------------------*/

// load Instagram embed script if needed, then process embeds
function load_instagram_embed_script() {
  if (window.instgrm?.Embeds?.process) {
    window.instgrm.Embeds.process();
    return;
  }

  if (window._instagram_embed_loading) return;

  window._instagram_embed_loading = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = '//www.instagram.com/embed.js';
  script.onload = () => {
    window._instagram_embed_loading = false;
    if (window.instgrm?.Embeds?.process) {
      window.instgrm.Embeds.process();
    }
  };
  script.onerror = () => {
    window._instagram_embed_loading = false;
  };

  document.body.appendChild(script);
}

// build Instagram embed (currently placeholder)
function build_instagram_embed(id) {
  return false;
}